import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { buildApiUrl } from '../config';

const PublicPostViewer = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Helper function to clean filename (remove timestamp/hash prefix and decode URL)
  const cleanFileName = (filename) => {
    // Remove the timestamp_hash_ prefix (e.g., "1762767962_48ef8443_")
    // The pattern matches: digits_hexadecimal_
    let cleaned = filename.replace(/^\d+_[a-fA-F0-9]+_/, '');
    // Decode URL encoding (e.g., %20 -> space)
    try {
      cleaned = decodeURIComponent(cleaned);
    } catch (e) {
      // If decoding fails, return as-is
      console.warn('Failed to decode filename:', filename);
    }
    return cleaned;
  };

  useEffect(() => {
    fetchPublicPost();
  }, [postId]);

  const fetchPublicPost = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/posts/public/${postId}`));
      
      if (!response.ok) {
        throw new Error(`Failed to fetch post: ${response.status}`);
      }
      
      const postData = await response.json();
      console.log('📮 Public post data received:', postData);
      console.log('🖼️ Media URLs:', postData.media_urls);
      setPost(postData);
    } catch (err) {
      console.error('Error fetching public post:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    } catch {
      return 'Recently';
    }
  };

  const getCategoryIcon = (category) => {
    const categoryIcons = {
      "school-announcement": "📢",
      "school-events": "🎉", 
      "trips": "🚌",
      "weekly-letters": "📝"
    };
    return categoryIcons[category] || "📝";
  };

  const getCategoryLabel = (category) => {
    const categoryLabels = {
      "school-announcement": "School Announcement",
      "school-events": "School Events",
      "trips": "Trips",
      "weekly-letters": "Weekly Letters"
      
    };
    return categoryLabels[category] || "Post";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Post Not Found</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">This post may have been removed or the link is invalid.</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📄</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Post Found</h1>
          <p className="text-gray-600">The requested post could not be loaded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">🏫</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">School Portal</h1>
              <p className="text-sm text-gray-600">Public Post View</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Post Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">{getCategoryIcon(post.category)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    {getCategoryLabel(post.category)}
                  </span>
                  {post.target_forms && post.target_forms.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {post.target_forms.map((form, i) => (
                        <span key={i} className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                          🎓 {form}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2 break-words">{post.title}</h1>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>📝 By {post.created_by}</span>
                  <span>🕒 {formatTime(post.timestamp)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Post Content */}
          <div className="p-6">
            <div className="prose max-w-none">
              <div 
                className="text-gray-800 break-words leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            {/* Media Files */}
            {post.media_urls && post.media_urls.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">📎 Attachments</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {post.media_urls.map((url, index) => {
                    // If URL is already complete, use it. Otherwise, construct the media URL
                    const imageUrl = url.startsWith('http') 
                      ? url 
                      : `${buildApiUrl('')}/api/posts/media/${url}`;
                    
                    return (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        {url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <img 
                            src={imageUrl}
                            alt={`Attachment ${index + 1}`}
                            className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setSelectedImage(imageUrl)}
                            onError={(e) => {
                              console.error('Failed to load image:', url);
                              console.log('Attempted URL:', e.target.src);
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMiA5VjEzTTEyIDE3SDE2TTggMTdIMTJNOCAxM0gxNk04IDlIMTZNNCA0SDE2TDIwIDhWMjBINFY0WiIgc3Ryb2tlPSIjOUNBM0FGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K';
                            }}
                          />
                        ) : (
                        <a 
                          href={imageUrl}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                        >
                          <span className="text-2xl">📄</span>
                          <span className="break-all">{cleanFileName(url.split('/').pop())}</span>
                        </a>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>📡 Shared from School Portal</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                📋 Copy Link
              </button>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>This is a public view of a school post. Some features may be limited.</p>
          <p>© 2025 School Portal - All rights reserved</p>
        </div>
      </main>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors z-10"
          >
            ×
          </button>
          <img
            src={selectedImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default PublicPostViewer;
